/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { get } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import Icon from '@material-ui/core/Icon';
import { List, useMediaQuery } from '@material-ui/core';
import { Visibility, Dashboard } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ScrollLinkItem } from './ScrollLinkItem';
import Button from '../common/Button';
import { getScrollSections } from '../../shared/submission/submissionPropertyAccessor';
import PaymentSummaryDrawerSection from '../../payment/components/drawer/summary/PaymentSummaryDrawerSection';
import { uiSchemaProperties } from '../../shared/enums/uiSchemaProperties';

const useStyles = makeStyles(theme => ({
  button: {
    width: 'calc(100% - 4rem)',
    margin: '0 2rem',
  },
  default: {
    position: 'inherit',
  },
  fixed: {
    margin: theme.spacing(2),
    top: '0',
    right: '0',
    position: 'fixed',
    zIndex: '999',
  },
  navigationSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  widgetContainer: {
    color: theme.palette.secondary.contrastText,
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    overflow: 'hidden',
  },
  widgetContainerDark: {
    color: theme.palette.secondary.contrastText,
    backgroundColor: '#E7EEF4',
  },
}));

export function ScrollSection({ previewDisabled, uiSchema, handlePreviewClick, progress, submission, className }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const isSmall = useMediaQuery(theme => theme.breakpoints.only('sm'));
  const [isPaymentSummaryVisible, setPaymentSummaryVisibility] = React.useState(true);

  const summaryVariant = get(
    uiSchema,
    `${uiSchemaProperties.UI_STYLINGVARIANTS}.${uiSchemaProperties.UI_SUMMARYBUTTON}`,
  );

  const hidePreviewButton = get(
    uiSchema,
    `${uiSchemaProperties.UI_SECTIONS}.${uiSchemaProperties.UI_HIDE_PREVIEW_BUTTON}`,
  );

  const hideSummary = get(uiSchema, `${uiSchemaProperties.UI_SECTIONS}.${uiSchemaProperties.UI_HIDE_SUMMARY}`);

  return (
    <div className={className}>
      {!isSmall && !hidePreviewButton && (
        // Setting a new custom variant as the requirement was to treat this button as an exception
        <Button
          disabled={previewDisabled}
          color={summaryVariant || 'primary'}
          variant="contained"
          className={classes.button}
          startIcon={<Visibility />}
          onClick={handlePreviewClick}
        >
          {t('submission.preview')}
        </Button>
      )}

      <nav aria-label={`${t('scroll.to')} ${t('navigation')}`} className={classes.navigationSection}>
        <List>
          {uiSchema &&
            getScrollSections(uiSchema).map((section, index) => {
              const config = get(uiSchema, section);
              return (
                !config?.disabledWorkflowSection && (
                  <div key={index}>
                    <ScrollLinkItem
                      key={section}
                      label={config['ui:title'] || section}
                      to={`submission_${section}`}
                      icon={<Icon>{config['ui:icon']}</Icon>}
                      progress={progress?.[section]?.percentage}
                      description={progress?.[section]?.messages}
                      hasDropdown={config.summarySection}
                      toggleDropdown={() => setPaymentSummaryVisibility(!isPaymentSummaryVisible)}
                      isSummaryVisible={isPaymentSummaryVisible}
                    />
                    {config.summarySection && (
                      <PaymentSummaryDrawerSection
                        uiSchema={uiSchema}
                        visible={isPaymentSummaryVisible}
                        submission={submission}
                        className={clsx(classes.widgetContainer, classes.widgetContainerDark)}
                      />
                    )}
                  </div>
                )
              );
            })}
        </List>
      </nav>

      {!hideSummary && (
        <nav aria-label={`${t('scroll.to')} ${t('summary')}`}>
          <List>
            <ScrollLinkItem label={t('summary')} to="summary" icon={<Dashboard />} />
          </List>
        </nav>
      )}
    </div>
  );
}
